import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import { subscribeToPush, checkPushSubscription, registerServiceWorker } from '../utils/pushNotifications';
import { Bell, X, Check, CheckCheck, BellOff } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

const safeDate = (d) => {
  try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM, hh:mm a') : ''; }
  catch { return ''; }
};

function playBellSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playNote = (freq, startTime, duration, gain = 0.4) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.8, startTime + duration);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playNote(587.3, now,        1.2, 0.35);
    playNote(739.9, now + 0.25, 1.2, 0.30);
    playNote(880.0, now + 0.5,  1.8, 0.25);
    setTimeout(() => ctx.close(), 3000);
  } catch (e) {}
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const prevUnreadRef = useRef(-1);
  const panelRef = useRef(null);

  // Setup push on mount
  useEffect(() => {
    registerServiceWorker();
    checkPushSubscription().then(subscribed => {
      setPushEnabled(subscribed);
      // Show prompt after 3 seconds if not subscribed and permission not denied
      if (!subscribed && Notification.permission === 'default') {
        setTimeout(() => setShowPushPrompt(true), 3000);
      }
    });
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      const newUnread = r.data.unread_count || 0;
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current !== -1) {
        playBellSound();
      }
      prevUnreadRef.current = newUnread;
      setNotifications(r.data.notifications || []);
      setUnread(newUnread);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleEnablePush = async () => {
    setShowPushPrompt(false);
    const success = await subscribeToPush();
    setPushEnabled(success);
    if (success) {
      // Test notification
      if (Notification.permission === 'granted') {
        new Notification('✅ Notifications Enabled!', {
          body: 'You will now receive a notification for every update',
          icon: '/Favicon.png',
        });
      }
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read', { ids: [] });
      setNotifications(n => n.map(notif => ({ ...notif, is_read: 1 })));
      setUnread(0); prevUnreadRef.current = 0;
    } catch {}
  };

  const markOneRead = async (id) => {
    try {
      await api.patch('/notifications/read', { ids: [id] });
      setNotifications(n => n.map(notif => notif.id === id ? { ...notif, is_read: 1 } : notif));
      setUnread(u => Math.max(0, u - 1));
      prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1);
    } catch {}
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      const wasUnread = notifications.find(n => n.id === id && !n.is_read);
      setNotifications(n => n.filter(notif => notif.id !== id));
      if (wasUnread) { setUnread(u => Math.max(0, u - 1)); prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1); }
    } catch {}
  };

  const handleBellClick = () => {
    setOpen(!open);
    if (!open && unread > 0) setTimeout(markAllRead, 1500);
  };

  const getTypeIcon = (type) => {
    if (type === 'stage_activated') return '🔔';
    if (type === 'task_completed') return '✅';
    if (type === 'item_completed') return '🎉';
    if (type === 'progress_update') return '📊';
    return '📋';
  };

  return (
    <>
      {/* Push Permission Prompt */}
      {showPushPrompt && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 text-sm">Enable Phone Notifications</p>
              <p className="text-xs text-gray-500 mt-1">Get notified directly on your phone when new work arrives or a stage is completed — even when the app is closed</p>
              <div className="flex gap-2 mt-3">
                <button onClick={handleEnablePush}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-2 rounded-xl text-xs font-bold transition-colors">
                  🔔 Enable Notifications
                </button>
                <button onClick={() => setShowPushPrompt(false)}
                  className="px-3 border border-gray-200 text-gray-500 hover:bg-gray-50 py-2 rounded-xl text-xs transition-colors">
                  Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bell Button */}
      <div className="relative" ref={panelRef}>
        <button onClick={handleBellClick}
          className="relative p-2 rounded-xl hover:bg-slate-700 text-slate-300 hover:text-white transition-colors">
          <Bell className={`w-5 h-5 ${unread > 0 ? 'text-amber-400' : ''}`} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-0.5 animate-pulse">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {/* Dropdown Panel */}
        {open && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-500" />
                <span className="font-bold text-gray-800 text-sm">Notifications</span>
                {unread > 0 && <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{unread}</span>}
              </div>
              <div className="flex items-center gap-2">
                {!pushEnabled && (
                  <button onClick={handleEnablePush} title="Enable phone notifications"
                    className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
                    <Bell className="w-3 h-3" /> Enable
                  </button>
                )}
                {notifications.length > 0 && (
                  <button onClick={markAllRead} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                    <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Push status */}
            {pushEnabled && (
              <div className="px-4 py-2 bg-green-50 border-b border-green-100 flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-green-600" />
                <span className="text-xs text-green-700 font-medium">Phone notifications active ✅</span>
              </div>
            )}

            {/* List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <div key={notif.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!notif.is_read ? 'bg-blue-50/50' : ''}`}>
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-100 text-sm">
                      {getTypeIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!notif.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{notif.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{safeDate(notif.created_at)}</p>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {!notif.is_read && (
                        <button onClick={() => markOneRead(notif.id)} className="text-blue-400 hover:text-blue-600 p-0.5">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => deleteOne(notif.id)} className="text-gray-300 hover:text-red-400 p-0.5">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-400">{notifications.length} notifications</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
